// src/components/Sidebar.tsx
'use client';

import Link from 'next/link';
import Image from 'next/image';
import styled, { css } from 'styled-components';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';
import { useLocalStorage } from '@/app/map/hooks/useLocalStorage';
import { MdAssignment, MdBarChart, MdPeople, MdSensors, MdChevronLeft, MdChevronRight, MdSettings, MdLogout, MdAddCircle } from 'react-icons/md';

/* ---------------------------------------------------------------------------
   Styled Components with Transient Props
   --------------------------------------------------------------------------- */

// Using transient props ($collapsed) ensures these props aren't passed to the DOM
interface SidebarContainerProps {
  $collapsed: boolean;
}

const SidebarContainer = styled.div<SidebarContainerProps>`
  background-color: #225055;
  color: white;
  width: ${({ $collapsed }) => ($collapsed ? '80px' : '250px')};
  padding: 1rem;
  box-sizing: border-box;
  height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: ${({ $collapsed }) => ($collapsed ? 'center' : 'flex-start')};
  transition: width 0.3s ease;
`;

interface LogoProps {
  $collapsed: boolean;
}

const LogoContainer = styled.div<LogoProps>`
  margin-bottom: 2rem;
  width: ${({ $collapsed }) => ($collapsed ? '40px' : '150px')};
  height: ${({ $collapsed }) => ($collapsed ? '40px' : '50px')};
  position: relative;
  transition: width 0.3s ease, height 0.3s ease;
  flex-shrink: 0;
`;

interface HeadingProps {
  $collapsed: boolean;
}

const Heading = styled.h2<HeadingProps>`
  font-size: 1.5rem;
  font-weight: bold;
  margin-bottom: 1rem;
  color: #fff;
  display: ${({ $collapsed }) => ($collapsed ? 'none' : 'block')};
`;

const StyledList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  width: 100%;
`;

interface StyledAnchorProps {
  $active: boolean;
  $collapsed: boolean;
  $disabled?: boolean;
}

const StyledAnchor = styled.a<StyledAnchorProps>`
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;
  color: white;
  text-decoration: none;
  font-size: 1.1rem;
  padding: 0.5rem;
  border-radius: 8px;
  width: 100%;
  transition: background-color 0.3s ease;
  background: none;
  border: none;
  cursor: pointer;

  /* Add this to ensure icon container is aligned */
  span {
    display: flex;
    align-items: center;
    margin-right: ${({ $collapsed }) => ($collapsed ? '0' : '0.8rem')};
  }

  &:hover {
    background-color: rgba(255, 255, 255, 0.2);
  }

  ${({ $active }) =>
    $active &&
    css`
      background-color: #f79824;
      color: white;
    `}

  ${({ $disabled }) =>
    $disabled &&
    css`
      opacity: 0.5;
      cursor: not-allowed;
      pointer-events: none;
      &:hover {
        background-color: transparent;
      }
    `}

  .link-text {
    display: ${({ $collapsed }) => ($collapsed ? 'none' : 'inline')};
  }
`;

interface ToggleButtonProps {
  $collapsed: boolean;
}

const ToggleButton = styled.button<ToggleButtonProps>`
  display: flex;
  align-items: center;
  background: none;
  border: none;
  color: white;
  font-size: 1.1rem;
  cursor: pointer;
  margin-top: auto;
  padding: 0.5rem;
  width: 100%;

  span {
    margin-right: ${({ $collapsed }) => ($collapsed ? '0' : '0.8rem')};
  }

  .toggle-text {
    display: ${({ $collapsed }) => ($collapsed ? 'none' : 'inline')};
  }
`;

/* ---------------------------------------------------------------------------
   Sidebar Component
   --------------------------------------------------------------------------- */

const Sidebar = () => {
  const pathname = usePathname();
  const [collapsed, setCollapsed] = useLocalStorage('sidebar-collapsed', false);
  const [hasSensorAccess, setHasSensorAccess] = useState(false);

  const toggleSidebar = () => {
    setCollapsed(!collapsed);
  };

  useEffect(() => {
    const loadAccess = async () => {
      try {
        const response = await fetch('/api/sensor-access', { cache: 'no-store' });
        if (!response.ok) {
          setHasSensorAccess(false);
          return;
        }
        const data = await response.json();
        setHasSensorAccess(Boolean(data?.allowed));
      } catch {
        setHasSensorAccess(false);
      }
    };

    loadAccess();
  }, []);

  return (
    <SidebarContainer $collapsed={collapsed}>
      <LogoContainer $collapsed={collapsed}>
        <Image
          src={collapsed ? "/LogoSmall.png" : "/Logo.png"}
          alt="Vigi Systems Logo"
          fill
          sizes="100%"
          style={{ objectFit: 'contain' }}
        />
      </LogoContainer>

      {/* Main navigation */}
      <StyledList style={{ flex: 1 }}>
        <li>
          <Link href="/" legacyBehavior passHref>
            <StyledAnchor $active={pathname === '/'} $collapsed={collapsed}>
              <span><MdAssignment size={28} /></span>
              <span className="link-text">Oppdrag</span>
            </StyledAnchor>
          </Link>
        </li>
        <li>
          <Link href="/reports" legacyBehavior passHref>
            <StyledAnchor $active={pathname === '/reports'} $collapsed={collapsed} $disabled>
              <span><MdBarChart size={28} /></span>
              <span className="link-text">Rapporter (kommer)</span>
            </StyledAnchor>
          </Link>
        </li>
        <li>
          <Link href="/sensors" legacyBehavior passHref>
            <StyledAnchor $active={pathname === '/sensors'} $collapsed={collapsed} $disabled={!hasSensorAccess}>
              <span><MdSensors size={28} /></span>
              <span className="link-text">Sensorer</span>
            </StyledAnchor>
          </Link>
        </li>
        {hasSensorAccess && (
          <li>
            <Link href="/create-sensor" legacyBehavior passHref>
              <StyledAnchor $active={pathname === '/create-sensor'} $collapsed={collapsed}>
                <span><MdAddCircle size={28} /></span>
                <span className="link-text">Produser sensor</span>
              </StyledAnchor>
            </Link>
          </li>
        )}
        <li>
          <Link href="/user-management" legacyBehavior passHref>
            <StyledAnchor $active={pathname === '/user-management'} $collapsed={collapsed}>
              <span><MdPeople size={28} /></span>
              <span className="link-text">Brukerstyring</span>
            </StyledAnchor>
          </Link>
        </li>
      </StyledList>

      {/* Bottom section */}
      <StyledList style={{ marginTop: 'auto', marginBottom: '1rem' }}>
        <li>
          <Link href="/settings" legacyBehavior passHref>
            <StyledAnchor $active={pathname === '/settings'} $collapsed={collapsed} $disabled>
              <span><MdSettings size={28} /></span>
              <span className="link-text">Innstillinger</span>
            </StyledAnchor>
          </Link>
        </li>
        <li>
          <StyledAnchor
            as="button"
            onClick={() => {
              // ✅ Trigger server route to clear cookies and redirect to FusionAuth
              window.location.href = '/api/custom';
            }}
            $active={false}
            $collapsed={collapsed}
          >
            <span><MdLogout size={28} /></span>
            <span className="link-text">Logg ut</span>
          </StyledAnchor>
        </li>
      </StyledList>

      <ToggleButton onClick={toggleSidebar} $collapsed={collapsed}>
        <span>{collapsed ? <MdChevronRight size={28} /> : <MdChevronLeft size={28} />}</span>
        <span className="toggle-text">Minimer meny</span>
      </ToggleButton>
    </SidebarContainer>
  );
};

export default Sidebar;
